import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { subTaskApi } from '../api/subTaskApi';
import { useUser } from '../contexts/UserContext';
import { CreateSubTaskRequest, UpdateSubTaskRequest } from '../types/todo';

/**
 * Hook to fetch sub-tasks for a parent todo item
 */
export const useSubTasks = (parentTodoItemId: number) => {
  const { currentUser } = useUser();

  return useQuery({
    queryKey: ['subTasks', parentTodoItemId],
    queryFn: () => subTaskApi.getSubTasks(parentTodoItemId, currentUser?.email || ''),
    enabled: !!currentUser?.email,
    staleTime: 1000, // Consider data stale after 1 second
  });
};

/**
 * Hook to create a new sub-task
 */
export const useCreateSubTask = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: (data: CreateSubTaskRequest) => 
      subTaskApi.createSubTask(data, currentUser?.email || ''),
    onSuccess: (_, variables) => {
      // Invalidate queries to refresh todo lists, todos, and specific sub-tasks
      queryClient.invalidateQueries({ queryKey: ['todoLists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['subTasks', variables.parentTodoItemId] });
    },
    onError: (error) => {
      console.error('Failed to create sub-task:', error);
    }
  });
};

/**
 * Hook to update an existing sub-task
 */
export const useUpdateSubTask = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateSubTaskRequest }) =>
      subTaskApi.updateSubTask(id, data, currentUser?.email || ''),
    onSuccess: (updatedSubTask) => {
      queryClient.invalidateQueries({ queryKey: ['todoLists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['subTasks', updatedSubTask.parentTodoItemId] });
    },
    onError: (error) => {
      console.error('Failed to update sub-task:', error);
    }
  });
};

/**
 * Hook to toggle sub-task completion
 */
export const useToggleSubTask = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: (id: number) => 
      subTaskApi.toggleSubTask(id, currentUser?.email || ''),
    onSuccess: (toggledSubTask) => {
      queryClient.invalidateQueries({ queryKey: ['todoLists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['subTasks', toggledSubTask.parentTodoItemId] });
    },
    onError: (error) => {
      console.error('Failed to toggle sub-task:', error);
    }
  });
};

/**
 * Hook to delete a sub-task
 */
export const useDeleteSubTask = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: (id: number) => 
      subTaskApi.deleteSubTask(id, currentUser?.email || ''),
    onSuccess: () => {
      // Delete doesn't return data, so invalidate all subTasks queries
      queryClient.invalidateQueries({ queryKey: ['todoLists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['subTasks'] });
    },
    onError: (error) => {
      console.error('Failed to delete sub-task:', error);
    }
  });
};

/**
 * Hook to reorder sub-tasks
 */
export const useReorderSubTasks = () => {
  const queryClient = useQueryClient();
  const { currentUser } = useUser();

  return useMutation({
    mutationFn: ({ parentTodoItemId, orderedIds }: { parentTodoItemId: number; orderedIds: number[] }) =>
      subTaskApi.reorderSubTasks(parentTodoItemId, orderedIds, currentUser?.email || ''),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['todoLists'] });
      queryClient.invalidateQueries({ queryKey: ['todos'] });
      queryClient.invalidateQueries({ queryKey: ['subTasks', variables.parentTodoItemId] });
    },
    onError: (error) => {
      console.error('Failed to reorder sub-tasks:', error);
    }
  });
};
